import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { useMeQuery } from './store/api'
import { AppShell } from './components/layout/AppShell'
import { Login } from './pages/auth/Login'
import { ChangePassword } from './pages/auth/ChangePassword'
import { PartnersList } from './pages/admin/PartnersList'
import { PartnerDetail } from './pages/admin/PartnerDetail'
import { Admins } from './pages/admin/Admins'
import { MonthEndClose } from './pages/admin/MonthEndClose'
import { PartnerInterest } from './pages/admin/PartnerInterest'
import { Overview } from './pages/partner/Overview'
import { Licences } from './pages/partner/Licences'
import { Settings } from './pages/partner/Settings'
import { Charges } from './pages/partner/Charges'
import { SmbSites } from './pages/partner/SmbSites'
import { Statements } from './pages/partner/Statements'
import { Glossary } from './pages/Glossary'
import { GroupSummary } from './pages/GroupSummary'
import { NoAccess } from './pages/NoAccess'
import { NotFound } from './pages/NotFound'
import type { Role } from '@shared/types'

/** Home route per role (design.md §3). */
function homePath(role: Role, firstSlug?: string, groupSlug?: string): string {
  if (role === 'atarim_admin') return '/admin/partners'
  if (role === 'group_user' && groupSlug) return `/g/${groupSlug}`
  return firstSlug ? `/p/${firstSlug}/overview` : '/no-access'
}

function Protected({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading, isError } = useMeQuery()
  const location = useLocation()
  if (isLoading) return <FullPageSpinner />
  if (isError || !me) return <Navigate to="/login" replace />
  if (me.mustChangePassword && location.pathname !== '/account/password') {
    return <Navigate to="/account/password" replace />
  }
  return <>{children}</>
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { data: me } = useMeQuery()
  if (me && me.role !== 'atarim_admin') return <NotFound />
  return <>{children}</>
}

/** partner_user navigating to a foreign slug gets 404 from the API; the shell handles it. */
function Home() {
  const { data: me, isLoading } = useMeQuery()
  if (isLoading || !me) return <FullPageSpinner />
  return <Navigate to={homePath(me.role, me.partners[0]?.slug, me.group?.slug)} replace />
}

function PartnerHome() {
  const { slug } = useParams()
  return <Navigate to={`/p/${slug}/overview`} replace />
}

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="size-6 animate-spin rounded-full border-2 border-border border-t-foreground" aria-label="Loading" />
    </div>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/account/password"
        element={
          <Protected>
            <ChangePassword />
          </Protected>
        }
      />
      <Route
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route path="/" element={<Home />} />
        <Route path="/admin" element={<Navigate to="/admin/partners" replace />} />
        <Route
          path="/admin/partners"
          element={
            <AdminOnly>
              <PartnersList />
            </AdminOnly>
          }
        />
        <Route
          path="/admin/partners/:slug"
          element={
            <AdminOnly>
              <PartnerDetail />
            </AdminOnly>
          }
        />
        <Route
          path="/admin/close"
          element={
            <AdminOnly>
              <MonthEndClose />
            </AdminOnly>
          }
        />
        <Route
          path="/admin/interest"
          element={
            <AdminOnly>
              <PartnerInterest />
            </AdminOnly>
          }
        />
        <Route
          path="/admin/admins"
          element={
            <AdminOnly>
              <Admins />
            </AdminOnly>
          }
        />
        <Route path="/p/:slug" element={<PartnerHome />} />
        <Route path="/p/:slug/overview" element={<Overview />} />
        <Route path="/p/:slug/licences" element={<Licences />} />
        <Route path="/p/:slug/sites" element={<SmbSites />} />
        <Route path="/p/:slug/charges" element={<Charges />} />
        <Route path="/p/:slug/statements" element={<Statements />} />
        <Route path="/p/:slug/settings" element={<Settings />} />
        <Route path="/p/:slug/settings/:tab" element={<Settings />} />
        <Route path="/g/:groupSlug" element={<GroupSummary />} />
        <Route path="/glossary" element={<Glossary />} />
        <Route path="/no-access" element={<NoAccess />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}
